import { observer } from "owa-mobx-react";
import React, { useCallback } from "react";
import loc, { format } from "owa-localize";
import classnames from "owa-classnames";
import { Tab, TabList, type SelectTabData, type SelectTabEvent } from "@fluentui/react-components";
import type { MailboxInfo } from "owa-client-types";
import { getDensityModeCssClass } from "owa-fabric-theme";
import { getStore as getListViewStore, getViewFilterForTable } from "owa-mail-list-store";
import { lazySelectFilter } from "owa-mail-filter-actions";
import { getViewFilterDisplay } from "owa-mail-list-filter-view";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import { useIsTypeRampUpdatesEnabled } from "owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import getTabKeyForViewFilter, { getViewFilterForTabKey, ALL_TAB_KEY, UNREAD_TAB_KEY, } from "../../utils/getTabKeyForViewFilter";
import { CopilotResultsTab } from "../../lazyFunctions";
import { unreadPivotAriaLabel } from "./UnreadPivot.locstring.json";

import { full, medium, compact, tabListRoot, tabListContainer, tab, tabText, tab_Win32Style, tabTextSelected_Win32Style, tabTextUnselected, tabTextUnselected_Win32Style, tabText_Win32Style, } from "./InboxPivotStyles.scss";

export interface UnreadPivotProps {
    tableViewId: string;
    
    /**
     * Name of the folder the pivot is shown for. The pivot replaces the folder name heading in the
     * list header, so the name is folded into the pivot's accessible name instead of being lost.
     */
    folderName: string;
    
    showCopilotResultsTab?: boolean;
    mailboxInfo?: MailboxInfo;
}

/**
 * Renders the Unread | All tab pair shown within mail folders.
 *
 * The pivot is an alternate presentation over the table query's `viewFilter`, so selecting a tab
 * simply reuses `selectFilter`. When a filter the pivot does not represent is active (for example
 * Flagged), neither tab is shown as selected; the filter menu button surfaces the active filter in
 * that case.
 */
const defaultExport: React.FunctionComponent<UnreadPivotProps> = observer(function UnreadPivot(props: UnreadPivotProps) {
    const isWin32Style = isWin32StylingEnabled();
    const isTypeRampEnabled = useIsTypeRampUpdatesEnabled();
    const densityModeCssClass = isTypeRampEnabled
        ? getDensityModeCssClass(full, medium, medium)
        : getDensityModeCssClass(full, medium, compact);
    
    const tableView = getListViewStore().tableViews.get(props.tableViewId);
    const selectedKey = getTabKeyForViewFilter(tableView ? getViewFilterForTable(tableView) : undefined);
    
    const baseTabContentClassName = useComputedValue(() => classnames(tabText, densityModeCssClass), [densityModeCssClass]);
    const tabContentProps = useComputedValue(() => ({
        className: classnames(baseTabContentClassName, isWin32Style && tabText_Win32Style, isWin32Style && tabTextSelected_Win32Style),
    }), [baseTabContentClassName, isWin32Style]);
    const unselectedTabContentProps = useComputedValue(() => ({
        className: classnames(baseTabContentClassName, isWin32Style && tabText_Win32Style, isWin32Style ? tabTextUnselected_Win32Style : tabTextUnselected),
    }), [baseTabContentClassName, isWin32Style]);
    
    const onTabSelect = useCallback((_ev: SelectTabEvent, data: SelectTabData) => {
        const viewFilter = getViewFilterForTabKey(data.value as string);
        lazySelectFilter.importAndExecute(viewFilter, "UnreadPivot");
    }, [props.tableViewId]);
    
    const tabClassName = classnames(tab, isWin32Style && tab_Win32Style);
    
    return (<div className={classnames(tabListContainer, densityModeCssClass)}>
            <TabList className={classnames(tabListRoot, densityModeCssClass)} selectedValue={selectedKey} onTabSelect={onTabSelect} aria-label={format(loc(unreadPivotAriaLabel), props.folderName)}>
                <Tab value={ALL_TAB_KEY} className={tabClassName} content={selectedKey === ALL_TAB_KEY ? tabContentProps : unselectedTabContentProps}>
                    {getViewFilterDisplay("All")}
                </Tab>
                <Tab value={UNREAD_TAB_KEY} className={tabClassName} content={selectedKey === UNREAD_TAB_KEY ? tabContentProps : unselectedTabContentProps}>
                    {getViewFilterDisplay("Unread")}
                </Tab>
                {props.showCopilotResultsTab && props.mailboxInfo && (<CopilotResultsTab tableViewId={props.tableViewId} mailboxInfo={props.mailboxInfo}/>)}
            </TabList>
        </div>);
}, "UnreadPivot");
export default defaultExport;
